import React, { useEffect, useMemo, useState } from "react";
import { ElasticVariant, ResultCardProductProps } from "@usereactify/search";
import { QuickAdd } from "./QuickAddCard";
import { addToCart, CustomProductData, getCustomProductData, getOptionKey } from "@app/scripts/utils";
import { ProductSwatches } from "@app/scripts/react-components/ProductSwatches";
import { XY } from "@app/scripts/types";
import { PersonalisationDrawer } from "@app/scripts/personalisation/components/PersonalisationDrawer";
import { update } from "lodash";

export type ProductCard = React.ComponentProps<NonNullable<ResultCardProductProps["render"]>>;

export const ProductCard: React.FC<ProductCard> = (props) => {
  const maxColourSwatches = 3;
  const [quickAddState, setQuickAddState] = useState(false);
  const [personalisationState, setPersonalisationState] = useState(false);
  const [selectedColour, setSelectedColour] = useState("");
  const [selectedVariant, setSelectedVariant] = useState(props?.product?.variants?.[0]);
  const personalisedProduct =
    props.product.tags.includes("Pen Personalisation") || props.product.tags.includes("Paper Personalisation");
  const [loading, setLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const [swatches, setSwatches] = useState<CustomProductData["swatches"]>([]);
  const [variantImages, setVariantImages] = useState<CustomProductData["variant_images"]>({});
  const [personalisationPos, setPersonalisationPos] = useState<XY>({ x: null, y: null });

  const [mainImage, setMainImage] = useState(props?.product?.image);
  const hoverImage = props.product.images?.[1]?.src;

  useEffect(() => {
    const getData = async () => {
      const data = await getCustomProductData(props.product.handle, props.product.variant_options);
      if (data?.swatches) setSwatches(data.swatches);
      if (data?.variant_images) setVariantImages(data.variant_images);
      if (data?.personalisation_position) setPersonalisationPos(data.personalisation_position);
    };
    getData();
  }, []);
  const [productPrice, updateProductPrice] = useState(props.price?.value)
  // The Elastic search formatted price includes country codes
  // This way uses the price and ignores counrty codes
  const productPriceString = productPrice ? productPrice % 1 === 0 ? `${productPrice}.00` : productPrice.toFixed(2) : ""
  const formattedCompareAtPriceString = props.compareAtPrice?.value ? props.compareAtPrice?.value % 1 === 0 ? `${props.compareAtPrice?.value}.00` : props.compareAtPrice?.value.toFixed(2) : ""
  const [formattedProductPrice, updateFormattedProductPrice] = useState(`$${productPriceString}`)
  const [formattedCompareAtPrice, updateFormattedCompareAtPrice] = useState(`$${formattedCompareAtPriceString}`)

  const colourOptions = useMemo(() => {``
    const optionKey = getOptionKey("colour", props.product.options);
    if (!optionKey) return [];
    const options = props.product.variants?.map((variant) => variant?.[optionKey])?.filter(Boolean);
    return [...new Set(options)];
  }, [props.product.options, props.product.variants]);

  const updateVariantVariables = (matchedVariant:ElasticVariant) => {
    updateProductPrice(matchedVariant.price);
    setSelectedVariant(matchedVariant);
    updateFormattedProductPrice(`$${matchedVariant.price}`)
    updateFormattedCompareAtPrice(`$${matchedVariant.compare_at_price}`)
  }

  const handleColorClick = (colour: string) => {
    setSelectedColour(colour);

    const matchedVariant = props.product?.variants?.find(
      (variant: any) =>
        variant.option1?.toLowerCase() === colour.toLowerCase() ||
        variant.option2?.toLowerCase() === colour.toLowerCase()
    );
    if (!matchedVariant?.id) return;
    updateVariantVariables(matchedVariant)
  };

  useEffect(() => {
    const variantImage = selectedVariant?.id && variantImages?.[selectedVariant?.id];
    if (variantImage) {
      setMainImage(`${variantImage}${variantImage.includes("?") ? "&" : "?"}width=340`);
    }
  }, [selectedVariant])

  const addToBag = async () => {
    setLoading(true);
    if (props.product.variants?.length && props.product.variants?.length > 1) {
      setQuickAddState(true);
    } else if (props.product.variants?.length === 1) {
      const variantId = props.product.variants[0].id;

      try {
        if (!variantId) throw "No variant id found";

        await addToCart({
          id: variantId,
          qty: 1,
        });
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    }
  };

  const closeQuickAdd = () => {
    setLoading(false);
    setQuickAddState(false);
  };

  const openPersonalisationDrawer = () => {
    setPersonalisationState(true);
  };

  const closePersonalisationDrawer = () => {
    setLoading(false);
    setPersonalisationState(false);
  };

  return (
    <>
      {quickAddState && (
        <QuickAdd
          product={props.product}
          initialVariant={selectedVariant}
          formattedPrice={formattedProductPrice}
          formattedCompareAtPrice={formattedCompareAtPrice}
          swatches={swatches}
          variantImages={variantImages}
          colourOptions={colourOptions}
          closeQuickAdd={closeQuickAdd}
          openPersonalisationDrawer={openPersonalisationDrawer}
          quickAddState={quickAddState}
          setTopLevelVariant={setSelectedVariant}
          onSale={props.onSale}
          updateVariantVariables={updateVariantVariables}
        />
      )}

      {personalisationState && (
        <PersonalisationDrawer
          mainImage={mainImage}
          closePersonalisation={closePersonalisationDrawer}
          personalisationState={personalisationState}
          selectedVariantId={selectedVariant?.id}
          personalisationPos={personalisationPos}
          productTags={props.product.tags}
        />
      )}

      <div ref={props.itemRef} className="rs__result-card-product">
        <a
          className="rs__result-card-product__link relative fade-in"
          onClick={props.handleClick}
          href={`/products/${props.product.handle}`}
        >
          {mainImage && (
            <img
              className="rs__result-card-product__image"
              src={mainImage}
              width="100%"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            />
          )}
          {hoverImage && (
            <img
              className={`rs__result-card-product__image transition-all absolute w-full top-0 left-0 ${
                isHovered ? "opacity-100" : "opacity-0"
              }`}
              src={hoverImage}
              width="100%"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              loading="lazy"
            />
          )}

          <product-flag
            tags={props.product.tags.join(",")}
            price={props.price?.toString()}
            compare_at_price={props.compareAtPrice?.toString()}
            available={(props?.product?.inventory_total > 0).toString()}
            class="absolute text-xs px-2 py-1"
          ></product-flag>

          <span className="rs__result-card-product__vendor">{props.product.vendor}</span>
          <span className="rs__result-card-product__title">{props.product.title}</span>
        </a>

        <div className="rs__result-card-product__colours">
          {colourOptions?.length > 0 ? (
            <ProductSwatches
              selectedColour={selectedColour}
              colourOptions={colourOptions}
              swatches={swatches}
              handleColorClick={handleColorClick}
              maxColourSwatches={maxColourSwatches}
              swatchStyle={{
                borderRadius: "50%",
                padding: "2px",
                height: "21px",
                width: "21px",
              }}
            />
          ) : (
            <div style={{ height: "21px" }} />
          )}
        </div>
        <div className="rs__result-card-product__prices">
          <span className={`rs__result-card-product__price ${props.onSale ? "text-brand-sale" : ""}`}>
            {props.price?.s?.symbol}
            {productPriceString}
          </span>

          {props.onSale && (
            <span className="rs__result-card-product__price-sale " style={{ textDecoration: "line-through" }}>
              {props.compareAtPrice?.s?.symbol}
              {props.compareAtPrice?.value}
            </span>
          )}
        </div>

        <div className={`rs__result-card-product__cta ${!personalisedProduct ? "justify-end" : "justify-between"}`}>
          {personalisedProduct ? (
            <button onClick={openPersonalisationDrawer} className="personalise_me">
              PERSONALISE ME
            </button>
          ) : (
            <span className="personalise_me"></span>
          )}
          <span className="flex items-center bg-[#F7F7F7] p-2 my-2 text-[11px] text-[#333333] justify-center gap-2 h-[34px] !font-mallory !font-medium">
            {!loading ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="14" viewBox="0 0 15 14" fill="none">
                <path d="M12.1997 7.00004H2.30025" stroke="#333333" stroke-width="1.5" stroke-linejoin="round" />
                <path d="M7.25 2.05029V11.9498" stroke="#333333" stroke-width="1.5" stroke-linejoin="round" />
              </svg>
            ) : (
              <></>
            )}

            {props?.product?.inventory_total <= 0 ? (
              <button
                className="swym-custom-watchlist rs__result-card-product__add-to-bag"
                data-swym-custom-watchlist
                data-url={`https://${Shopify.shop}/products/${props.product.handle}`}
                data-variant-id={selectedVariant?.id}
                data-product-id={props.product.id}
              >
                NOTIFY ME
              </button>
            ) : (
              <button onClick={addToBag} disabled={loading} className="rs__result-card-product__add-to-bag">
                {loading ? "ADDING TO BAG..." : "ADD TO BAG"}
              </button>
            )}
          </span>
        </div>
      </div>
    </>
  );
};
